// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Guild} from '@app/features/guild/models/Guild';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {type GuildRoleColors, staffRolePosition} from '@app/features/name_style/NameStyle';
import type {User} from '@app/features/user/models/User';
import type {NameStyle} from '@fluxer/schema/src/domains/user/PlusAppearanceSchemas';

export interface GuildNameStyleProps {
	nameStyle: NameStyle | null;
	guildRoles: GuildRoleColors | undefined;
	guildOwnerRoleIds: ReadonlySet<string> | undefined;
	staffPosition: number | undefined;
}

// The roles the guild's owner holds, undefined while the owner's member is not loaded.
export function guildOwnerRoleIds(guild: Guild | null | undefined): ReadonlySet<string> | undefined {
	return guild ? GuildMembers.getMember(guild.id, guild.ownerId)?.roles : undefined;
}

// How far the member's own standing reaches: every role for the guild's owner, else the member's highest staff role;
// undefined for a member who is not staff, and without the member loaded only the owner is known.
export function guildStaffPosition(
	guild: Guild | null | undefined,
	userId: string,
	memberRoleIds: ReadonlySet<string> | undefined,
): number | undefined {
	if (!guild) return undefined;
	if (guild.ownerId === userId) return Number.POSITIVE_INFINITY;
	return memberRoleIds ? staffRolePosition(guild.roles, memberRoleIds) : undefined;
}

// For a name in a guild surface: the whole style draws over a role colour, except colours that could pass for one of
// the guild's coloured staff roles above the member's own standing, which give way to the surface's colour.
export function guildNameStyleProps(user: User, guildId: string): GuildNameStyleProps {
	const nameStyle = user.nameStyle;
	if (!nameStyle) return {nameStyle, guildRoles: undefined, guildOwnerRoleIds: undefined, staffPosition: undefined};
	const guild = Guilds.getGuild(guildId);
	return {
		nameStyle,
		guildRoles: guild?.roles,
		guildOwnerRoleIds: guildOwnerRoleIds(guild),
		staffPosition: guildStaffPosition(
			guild,
			user.id,
			guild ? GuildMembers.getMember(guild.id, user.id)?.roles : undefined,
		),
	};
}
